import { Check, ListFilter } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Button } from "@/shared/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/components/ui/command";
import { cn } from "@/shared/lib/utils";
import { useListCompanyQuery } from "@/services/backend/company/api/company";
import t from "@/lang/lang";
import { Filter } from "@/shared/lib/filter/filter";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Spinner } from "@/shared/components/ui/spinner";

export interface CompanyFilterProps {
  filter: Filter<string> | undefined;
  onFilterChange: (filter: Filter<string>) => void;
  hidden?: boolean;
}

export function CompanyFilter({
  filter,
  onFilterChange,
  hidden,
}: CompanyFilterProps) {
  // This allows us to accept undefined filters,
  // which is common in list requests. This makes
  // it nicer to call the component.
  const filterVal = filter ?? { active: false, values: [] };
  // simplifies some code below
  const currentValues = filterVal.values ?? [];

  const { data: companyList, isLoading, error } = useListCompanyQuery({});

  if (hidden) {
    return null;
  }

  // We hide this for users with only one company
  if (companyList && companyList.companies.length <= 1) {
    return null;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
          <ListFilter />
          {t("Unternehmen")}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0" align="start">
        <Command>
          <CommandInput placeholder={t("Suchen")} />
          {isLoading && (
            <span className="flex w-full items-center justify-center gap-2 py-3 text-sm">
              <Spinner />
              {t("Lade Daten ...")}
            </span>
          )}
          {error && (
            <RTKQueryErrorAlert error={error} className="border-none" />
          )}
          {companyList && (
            <CommandList>
              <CommandEmpty className="py-3 text-center text-sm">
                {companyList.companies.length === 0
                  ? t("Keine Unternehmen")
                  : t("Nicht gefunden")}
              </CommandEmpty>
              <CommandGroup>
                {companyList.companies.map((company) => (
                  <CommandItem
                    key={company.id}
                    value={company.name}
                    onSelect={() => {
                      const values = currentValues.includes(company.id)
                        ? currentValues.filter((v) => v !== company.id)
                        : [...currentValues, company.id];
                      onFilterChange({
                        active: values.length > 0,
                        values,
                      });
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        currentValues.includes(company.id)
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                    {company.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
